/**
 * Reports > Cash & Credit (/reports/cash-credit).
 *
 * Cash and credit sales by store for a range of business days (the last complete fiscal
 * week unless a range is chosen), by store or by store and day, with tiles, a store
 * filter, a totals row and an Excel download. Replaces the QuickSight dashboard "Cash &
 * Credit Sales by Store".
 *
 * This page is deliberately thin. The screen is the shared `CashCredit` component in
 * `@ampersandbrands/ui` (presentational); this file fetches through `useCashCredit`,
 * keeps the range, the grain and the store filter in the URL so a view is a shareable
 * link, and adds the page chrome: header with the pin toggle and the Excel download.
 *
 * RBAC: head office only, `reports.cashcredit`. There is no store-manager view, so a
 * store account never sees the page (and the api answers 403 if it asks).
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available yet"
 * card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CashCredit as CashCreditScreen, type CashCreditGrain, type FinanceDateRange } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import {
  buildFinanceQs,
  denverToday,
  downloadFinanceXlsx,
  parseGrain,
  parseRange,
  parseStores,
  useCashCredit,
  writeGrain,
  writeRange,
  writeStores,
} from '@/lib/useFinance';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report. Head office only. */
const SLUG = 'reports.cashcredit';

function CashCreditInner() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, SLUG, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlRange = useMemo(() => parseRange(searchParams), [searchParams]);
  const stores = useMemo(() => parseStores(searchParams), [searchParams]);
  const grain = useMemo(() => parseGrain(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useCallback(
    (fn: (sp: URLSearchParams) => void) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          fn(sp);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useCashCredit(urlRange, stores, grain, canView);
  const shownRange = urlRange ?? dataQ.range;

  const pageState = useMemo(
    () => [
      { label: 'Business days', value: shownRange ? `${shownRange.from} to ${shownRange.to}` : 'Last complete fiscal week' },
      { label: 'Stores', value: stores.length > 0 ? stores.join(', ') : 'All stores' },
      { label: 'Rows', value: grain === 'day' ? 'By store and day' : 'By store' },
    ],
    [shownRange, stores, grain],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadFinanceXlsx('cash-credit/export', buildFinanceQs(urlRange, stores), 'cash-and-credit.xlsx');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Cash & Credit" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Cash & Credit"
      subtitle="Cash and credit sales by store for a range of business days. Cash is payments with pay code type B and credit is card payments (type E or D). Without a range it shows the last complete fiscal week, Sunday to Saturday. Refreshed from CounterPoint once a day."
      actions={<ToolPinToggle toolSlug="cash-credit" toolName="Cash & Credit" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Cash & Credit" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <CashCreditScreen
        rows={dataQ.rows}
        summary={dataQ.summary}
        storeOptions={dataQ.stores}
        range={shownRange}
        maxDate={denverToday()}
        asOf={dataQ.asOf}
        grain={grain}
        onGrainChange={(g: CashCreditGrain) => update((sp) => writeGrain(sp, g))}
        onRangeChange={(r: FinanceDateRange | null) => update((sp) => writeRange(sp, r))}
        filters={{ store_no: stores }}
        onFiltersChange={(f) => update((sp) => writeStores(sp, f.store_no))}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        onExport={() => void handleExport()}
        exporting={exporting}
      />
    </div>
  );
}

export function CashCredit() {
  return (
    <ErrorBoundary>
      <CashCreditInner />
    </ErrorBoundary>
  );
}
